import { baseApi } from "../app-main/store/baseApi";

export type ReviewerType = "host" | "guest" | "attendee";
export type ReviewType = "guest" | "space";

export type ReviewName = {
  first?: string;
  last?: string;
};

export type ReviewGuest = {
  avatar_id?: string;
  name?: ReviewName;
};

export type ReviewHost = {
  avatar_id?: string;
  name?: ReviewName;
};

export type ReviewListingImage = {
  external_id?: string;
};

export type ReviewListing = {
  images?: ReviewListingImage[];
  title?: string;
};

export type ReviewBooking = {
  start?: {
    display?: {
      date?: string;
    };
  };
};

/**
 * A single pending review from `GET /v1/reviews/user/:ssoId/current`. The
 * endpoint returns a direct array of these (not wrapped in an object) and
 * includes the nested `guest`/`host`/`listing`/`booking` objects the review
 * modal needs to render.
 */
export type CurrentReview = {
  _id: string;
  booking_id: string;
  booking?: ReviewBooking;
  complete: boolean;
  date_created?: string;
  expired?: boolean;
  guest?: ReviewGuest;
  guest_id?: string;
  host?: ReviewHost;
  host_id?: string;
  listing?: ReviewListing;
  public?: boolean;
  review_type: ReviewType;
  reviewer_type: ReviewerType;
  space_id: string;
  use_type?: string;
};

export type GetUserCurrentReviewsArgs = {
  ssoId: string;
};

export type ReviewSpace = {
  images?: ReviewListingImage[];
  title?: string;
};

/**
 * A single completed, public review returned by `GET /v1/reviews/space/:spaceId`
 * (reviews guests left about a space). `reviewer_name` is a Mixed type on the
 * API and may be a string or a name object.
 */
export type SpaceReview = {
  _id: string;
  complete?: boolean;
  date_created?: string;
  listing?: ReviewListing;
  public?: boolean;
  review_object_id?: string;
  review_text?: string;
  review_type?: ReviewType;
  reviewer_avatar_id?: string;
  reviewer_name?: string | ReviewName;
  reviewer_type?: ReviewerType;
  space?: ReviewSpace;
  stars?: number;
};

export type SpaceReviewsResponse = {
  reviews?: SpaceReview[];
  structuredReviews?: unknown[];
  total_count: number;
};

/** One space's reviews within a batched `getSpaceReviewsBatch` response. */
export type SpaceReviewsBatchEntry = {
  reviews: SpaceReview[];
  spaceId: string;
};

export type GetSpaceReviewsBatchArgs = {
  spaceIds: string[];
};

export const reviewsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getUserCurrentReviews: builder.query<
      CurrentReview[],
      GetUserCurrentReviewsArgs
    >({
      query: ({ ssoId }) => ({
        url: `/v1/reviews/user/${ssoId}/current`,
      }),
    }),

    // ReviewsAPI exposes reviews one space at a time, so the batch is fetched
    // in parallel inside a `queryFn`. Per-space failures degrade to an empty
    // list rather than failing the whole batch (the Host Hub just shows fewer
    // spaces). The host's space set is capped upstream by the caller.
    getSpaceReviewsBatch: builder.query<
      SpaceReviewsBatchEntry[],
      GetSpaceReviewsBatchArgs
    >({
      queryFn: async ({ spaceIds }, _queryApi, _extraOptions, fetchWithBQ) => {
        const entries = await Promise.all(
          spaceIds.map(async (spaceId) => {
            const result = await fetchWithBQ(`/v1/reviews/space/${spaceId}`);
            // The endpoint returns a bare array of reviews by default, and only
            // wraps them in `{ reviews, total_count }` when `include_total` is
            // requested. Normalise both shapes.
            const data = result.data as
              | SpaceReview[]
              | SpaceReviewsResponse
              | undefined;
            const reviews = Array.isArray(data) ? data : (data?.reviews ?? []);
            return {
              reviews,
              spaceId,
            };
          }),
        );
        return { data: entries };
      },
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetSpaceReviewsBatchQuery,
  useGetUserCurrentReviewsQuery,
} = reviewsApi;
